import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  Alert,
  Snackbar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import KanbanBoard from "./components/KanbanBoard";
import boardService from "../../services/boardService";
import pinnedBoardService from "../../services/pinnedBoardService";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`board-tabpanel-${index}`}
      aria-labelledby={`board-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const BoardsPage = () => {
  const location = useLocation();

  // State for loading
  const [loading, setLoading] = useState(true);

  // State for active board tab
  const [boardTabValue, setBoardTabValue] = useState(0);

  // State for boards
  const [boards, setBoards] = useState([]);

  // State for tracking drag operation
  const [isDragging, setIsDragging] = useState(false);
  const [draggedTabIndex, setDraggedTabIndex] = useState(null);

  // Set up drag state handlers
  const handleDragStart = useCallback((index) => {
    setIsDragging(true);
    setDraggedTabIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedTabIndex(null);
  }, []);

  // State for board menu
  const [boardMenuAnchorEl, setBoardMenuAnchorEl] = useState(null);
  const [currentBoardId, setCurrentBoardId] = useState(null);

  // State for add/edit board dialog
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [boardDialogMode, setBoardDialogMode] = useState("add"); // "add" or "edit"
  const [boardFormData, setBoardFormData] = useState({
    name: "",
    description: "",
    template: "offerStatus",
  });

  // State for error handling
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // State for pinned boards
  const [pinnedBoards, setPinnedBoards] = useState([]);

  // Use sandbox mode for development/testing
  const useSandboxMode = true; // Set to false when API is ready

  // Fetch boards data
  useEffect(() => {
    const fetchBoards = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use boardService to get boards (works in both sandbox and production modes)
        const fetchedBoards = await boardService.getBoards();
        setBoards(fetchedBoards);

        // Fetch pinned boards
        try {
          const pinnedBoardsData = await pinnedBoardService.getPinnedBoards();
          setPinnedBoards(pinnedBoardsData);
        } catch (err) {
          console.error("Error fetching pinned boards:", err);
          // Don't fail the entire request if pinned boards fail
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching boards:", err);
        setLoading(false);
      }
    };

    fetchBoards();
  }, []);

  // Handle board tab change
  const handleBoardTabChange = (event, newValue) => {
    setBoardTabValue(newValue);
  };

  // Handle board menu open
  const handleBoardMenuOpen = (event, boardId) => {
    setBoardMenuAnchorEl(event.currentTarget);
    setCurrentBoardId(boardId);
  };

  // Handle board menu close
  const handleBoardMenuClose = () => {
    setBoardMenuAnchorEl(null);
    setCurrentBoardId(null);
  };

  // Handle opening add board dialog
  const handleAddBoardDialogOpen = () => {
    setBoardFormData({
      name: "",
      description: "",
      template: "offerStatus",
    });
    setBoardDialogMode("add");
    setBoardDialogOpen(true);
  };

  // Handle opening edit board dialog
  const handleEditBoardDialogOpen = () => {
    const board = boards.find((b) => b.id === currentBoardId);
    if (board) {
      setBoardFormData({
        name: board.name,
        description: board.description || "",
      });
      setBoardDialogMode("edit");
      setBoardDialogOpen(true);
    }
    handleBoardMenuClose();
  };

  // Handle closing board dialog
  const handleBoardDialogClose = () => {
    setBoardDialogOpen(false);
  };

  // Handle board form input change
  const handleBoardFormChange = (e) => {
    const { name, value } = e.target;
    setBoardFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle save board
  const handleSaveBoard = async () => {
    if (boardFormData.name.trim() === "") {
      setError("Board name is required");
      return;
    }

    setError(null);

    try {
      if (boardDialogMode === "add") {
        // Add new board using boardService
        const newBoardData = {
          name: boardFormData.name.trim(),
          description: boardFormData.description.trim(),
          template: boardFormData.template,
        };

        const newBoard = await boardService.createBoard(newBoardData);

        setBoards((prev) => [...prev, newBoard]);
        setBoardTabValue(boards.length); // Switch to new board

        // Show success message
        setSnackbar({
          open: true,
          message: "Board created successfully",
          severity: "success",
        });
      } else {
        // Edit existing board
        // Note: In a real app, we would use boardService.updateBoard
        // For now, just update the state since we haven't implemented updateBoard yet
        const updatedBoards = boards.map((board) =>
          board.id === currentBoardId
            ? {
                ...board,
                name: boardFormData.name.trim(),
                description: boardFormData.description.trim(),
              }
            : board
        );
        setBoards(updatedBoards);

        // Show success message
        setSnackbar({
          open: true,
          message: "Board updated successfully",
          severity: "success",
        });
      }

      handleBoardDialogClose();
    } catch (error) {
      console.error("Error saving board:", error);
      setError("Failed to save board. Please try again.");
    }
  };

  // Handle delete board
  const handleDeleteBoard = async () => {
    try {
      // Delete the board using boardService
      await boardService.deleteBoard(currentBoardId);

      // Update local state
      const filteredBoards = boards.filter(
        (board) => board.id !== currentBoardId
      );
      setBoards(filteredBoards);

      // Show success message
      setSnackbar({
        open: true,
        message: "Board deleted successfully",
        severity: "success",
      });

      // Close menu and reset current board
      handleBoardMenuClose();
      setCurrentBoardId(null);

      // If we deleted the current board, switch to the first available board
      if (filteredBoards.length > 0 && boardTabValue >= filteredBoards.length) {
        setBoardTabValue(0);
      }
    } catch (error) {
      console.error("Error deleting board:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete board. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle pin board
  const handlePinBoard = async () => {
    try {
      await pinnedBoardService.pinBoard(currentBoardId);

      // Find the board to add to pinned boards
      const boardToPin = boards.find((board) => board.id === currentBoardId);
      if (boardToPin) {
        // Update pinned boards state with the board object
        const updatedPinnedBoards = [...pinnedBoards, boardToPin];
        setPinnedBoards(updatedPinnedBoards);
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Board pinned to dashboard successfully",
        severity: "success",
      });

      handleBoardMenuClose();
    } catch (error) {
      console.error("Error pinning board:", error);
      setSnackbar({
        open: true,
        message: "Failed to pin board. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle unpin board
  const handleUnpinBoard = async () => {
    try {
      await pinnedBoardService.unpinBoard(currentBoardId);

      // Update pinned boards state by removing the board
      const updatedPinnedBoards = pinnedBoards.filter(
        (pinnedBoard) =>
          pinnedBoard._id !== currentBoardId &&
          pinnedBoard.id !== currentBoardId
      );
      setPinnedBoards(updatedPinnedBoards);

      // Show success message
      setSnackbar({
        open: true,
        message: "Board unpinned from dashboard successfully",
        severity: "success",
      });

      handleBoardMenuClose();
    } catch (error) {
      console.error("Error unpinning board:", error);
      setSnackbar({
        open: true,
        message: "Failed to unpin board. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle board columns update
  const handleBoardColumnsUpdate = useCallback(
    async (boardId, updatedColumns) => {
      try {
        // Update the board in the backend
        const updatedBoard = await boardService.updateBoard(boardId, {
          columns: updatedColumns,
        });

        // Update local boards state
        setBoards((prevBoards) =>
          prevBoards.map((board) =>
            board.id === boardId ? updatedBoard : board
          )
        );

        // If this board is pinned, also update the pinned boards state
        setPinnedBoards((prevPinnedBoards) =>
          prevPinnedBoards.map((pinnedBoard) => {
            if (pinnedBoard._id === boardId || pinnedBoard.id === boardId) {
              return updatedBoard;
            }
            return pinnedBoard;
          })
        );
      } catch (error) {
        console.error("Error updating board:", error);
        // You could show a toast notification here
      }
    },
    []
  );

  // Handle individual recruit updates (color, notes)
  const handleRecruitUpdate = useCallback(
    async (boardId, recruitId, updates) => {
      try {
        // Find the column that contains this recruit
        const board = boards.find((b) => b.id === boardId);
        if (!board) return;

        let columnId = null;
        for (const column of board.columns) {
          if (
            column.recruits?.some(
              (r) => r.id === recruitId || r._id === recruitId
            )
          ) {
            columnId = column.id;
            break;
          }
        }

        if (columnId && recruitId) {
          // Update the individual recruit in the backend
          await boardService.updateRecruit(
            boardId,
            columnId,
            recruitId,
            updates
          );

          // Update local state immediately instead of refreshing all boards
          // Preserve the original data structure - don't transform it
          setBoards((prevBoards) =>
            prevBoards.map((b) => {
              if (b.id === boardId) {
                return {
                  ...b,
                  columns: b.columns.map((col) => {
                    if (col.id === columnId) {
                      return {
                        ...col,
                        recruits: col.recruits.map((r) =>
                          r.id === recruitId || r._id === recruitId
                            ? { ...r, ...updates }
                            : r
                        ),
                      };
                    }
                    return col;
                  }),
                };
              }
              return b;
            })
          );

          // Also update pinned boards if this board is pinned
          setPinnedBoards((prevPinnedBoards) =>
            prevPinnedBoards.map((pinnedBoard) => {
              if (pinnedBoard._id === boardId || pinnedBoard.id === boardId) {
                // Find and update the same board in pinned boards
                const board = boards.find((b) => b.id === boardId);
                if (board) {
                  return {
                    ...board,
                    columns: board.columns.map((col) => {
                      if (col.id === columnId) {
                        return {
                          ...col,
                          recruits: col.recruits.map((r) =>
                            r.id === recruitId || r._id === recruitId
                              ? { ...r, ...updates }
                              : r
                          ),
                        };
                      }
                      return col;
                    }),
                  };
                }
              }
              return pinnedBoard;
            })
          );
        }
      } catch (error) {
        console.error("Error updating recruit:", error);
      }
    },
    [boards]
  );

  // Create callbacks for each board
  const boardUpdateCallbacks = useMemo(() => {
    const callbacks = {};
    boards.forEach((board) => {
      callbacks[board.id] = (updatedColumns) => {
        handleBoardColumnsUpdate(board.id, updatedColumns);
      };
    });
    return callbacks;
  }, [boards, handleBoardColumnsUpdate]);

  // Handle reordering boards
  const handleReorderBoards = async (sourceIndex, destinationIndex) => {
    // Create a new array with the reordered boards
    const reorderedBoards = [...boards];
    const [movedBoard] = reorderedBoards.splice(sourceIndex, 1);
    reorderedBoards.splice(destinationIndex, 0, movedBoard);

    // Update the boards state immediately for responsive UI
    setBoards(reorderedBoards);

    // Update the active tab value if needed
    if (boardTabValue === sourceIndex) {
      setBoardTabValue(destinationIndex);
    } else if (
      boardTabValue > sourceIndex &&
      boardTabValue <= destinationIndex
    ) {
      setBoardTabValue(boardTabValue - 1);
    } else if (
      boardTabValue < sourceIndex &&
      boardTabValue >= destinationIndex
    ) {
      setBoardTabValue(boardTabValue + 1);
    }

    // Persist the board order to the backend
    try {
      const boardOrders = reorderedBoards.map((board, index) => ({
        boardId: board.id,
        order: index,
      }));

      await boardService.updateBoardOrder(boardOrders);
    } catch (error) {
      console.error("Error updating board order:", error);
      // Optionally show an error message to the user
      setSnackbar({
        open: true,
        message: "Failed to save board order. Please try again.",
        severity: "error",
      });

      // Revert the local changes on error
      const revertedBoards = [...boards];
      const [revertedBoard] = revertedBoards.splice(destinationIndex, 1);
      revertedBoards.splice(sourceIndex, 0, revertedBoard);
      setBoards(revertedBoards);
    }
  };

  // Effect to handle boardId query parameter
  useEffect(() => {
    const boardIdParam = new URLSearchParams(location.search).get("boardId");
    if (boardIdParam) {
      const boardIndex = boards.findIndex((board) => board.id === boardIdParam);
      if (boardIndex !== -1) {
        setBoardTabValue(boardIndex);
      } else {
        // If boardId is invalid, reset to default or show error
        setBoardTabValue(0);
        console.warn(`Board with ID ${boardIdParam} not found.`);
      }
    }
  }, [location.search, boards]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              Recruiting Boards
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Organize and track your recruiting prospects with customizable
              boards
            </Typography>
            {/* Mobile view: Button below subtitle */}
            <Box sx={{ display: { xs: "block", sm: "none" }, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddBoardDialogOpen}
              >
                New Board
              </Button>
            </Box>
          </Grid>
          {/* Desktop view: Button to the right */}
          <Grid item sx={{ display: { xs: "none", sm: "block" } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddBoardDialogOpen}
            >
              New Board
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : boards.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            No boards yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first recruiting board to start organizing prospects
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBoardDialogOpen}
          >
            Create Board
          </Button>
        </Paper>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={boardTabValue}
              onChange={handleBoardTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="recruiting boards tabs"
            >
              {boards.map((board, index) => (
                <DraggableTab
                  key={board.id}
                  board={board}
                  index={index}
                  onMenuOpen={handleBoardMenuOpen}
                  isDragging={isDragging && draggedTabIndex === index}
                  onReorder={handleReorderBoards}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onTabClick={() => handleBoardTabChange(null, index)}
                />
              ))}
            </Tabs>
          </Box>

          {boards.map((board, index) => (
            <TabPanel key={board.id} value={boardTabValue} index={index}>
              {board.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {board.description}
                </Typography>
              )}
              <KanbanBoard
                key={`kanban-${board.id}`}
                columns={board.columns}
                onColumnsUpdate={(updatedColumns) => {
                  return boardUpdateCallbacks[board.id](updatedColumns);
                }}
                onRecruitUpdate={(recruitId, updates) =>
                  handleRecruitUpdate(board.id, recruitId, updates)
                }
              />
            </TabPanel>
          ))}
        </>
      )}

      {/* Board menu */}
      <Menu
        anchorEl={boardMenuAnchorEl}
        open={Boolean(boardMenuAnchorEl)}
        onClose={handleBoardMenuClose}
      >
        <MenuItem onClick={handleEditBoardDialogOpen}>Edit Board</MenuItem>
        {pinnedBoards.some(
          (pinnedBoard) =>
            pinnedBoard._id === currentBoardId ||
            pinnedBoard.id === currentBoardId
        ) ? (
          <MenuItem onClick={handleUnpinBoard}>
            <PushPinIcon sx={{ mr: 1, fontSize: 16 }} />
            Unpin from Dashboard
          </MenuItem>
        ) : (
          <MenuItem onClick={handlePinBoard}>
            <PushPinOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
            Pin to Dashboard
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDeleteBoard} sx={{ color: "error.main" }}>
          Delete Board
        </MenuItem>
      </Menu>

      {/* Add/Edit Board Dialog */}
      <Dialog
        open={boardDialogOpen}
        onClose={handleBoardDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {boardDialogMode === "add" ? "Create New Board" : "Edit Board"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Board Name"
            type="text"
            fullWidth
            variant="outlined"
            value={boardFormData.name}
            onChange={handleBoardFormChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={boardFormData.description}
            onChange={handleBoardFormChange}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          {boardDialogMode === "add" && (
            <FormControl component="fieldset" sx={{ mt: 1 }}>
              <FormLabel component="legend">Template</FormLabel>
              <RadioGroup
                row
                name="template"
                value={boardFormData.template}
                onChange={handleBoardFormChange}
              >
                <FormControlLabel
                  value="offerStatus"
                  control={<Radio />}
                  label="Offer Status"
                />
                <FormControlLabel
                  value="blank"
                  control={<Radio />}
                  label="Blank Board"
                />
                <FormControlLabel
                  value="fivePositions"
                  control={<Radio />}
                  label="5 Positions (RB, LB, QB, TE, WR)"
                />
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBoardDialogClose}>Cancel</Button>
          <Button
            onClick={handleSaveBoard}
            variant="contained"
            disabled={!boardFormData.name.trim()}
          >
            {boardDialogMode === "add" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Draggable Tab component
const DraggableTab = ({
  board,
  index,
  onMenuOpen,
  isDragging,
  onReorder,
  onDragStart,
  onDragEnd,
  onTabClick,
}) => {
  const tabRef = useRef(null);
  const dragHandleRef = useRef(null);

  useEffect(() => {
    const element = dragHandleRef.current;
    if (!element) return;

    // Make the tab draggable by the drag handle only
    const draggableTab = draggable({
      element,
      getInitialData: () => ({
        type: "board-tab",
        boardId: board.id,
        sourceIndex: index,
      }),
      onDragStart: () => {
        if (tabRef.current) {
          tabRef.current.style.opacity = "0.8";
          tabRef.current.style.backgroundColor = "#1976d2"; // Dark blue background
          tabRef.current.style.color = "white";
        }
        element.style.cursor = "grabbing";
        onDragStart(index);
      },
      onDrop: () => {
        if (tabRef.current) {
          tabRef.current.style.opacity = "";
          tabRef.current.style.backgroundColor = "";
          tabRef.current.style.color = "";
        }
        element.style.cursor = "";
        onDragEnd();
      },
    });

    // Make the tab a drop target for other tabs
    const dropTarget = dropTargetForElements({
      element: tabRef.current,
      canDrop: ({ source }) => source.data.type === "board-tab",
      getData: () => ({
        targetIndex: index,
      }),
      onDragEnter: () => {
        if (tabRef.current) {
          tabRef.current.style.backgroundColor = "rgba(25, 118, 210, 0.2)";
        }
      },
      onDragLeave: () => {
        if (tabRef.current) {
          tabRef.current.style.backgroundColor = "";
        }
      },
      onDrop: ({ source }) => {
        if (tabRef.current) {
          tabRef.current.style.backgroundColor = "";
        }
        const { boardId, sourceIndex } = source.data;

        // Don't do anything if dropping on itself
        if (sourceIndex === index) return;

        onReorder(sourceIndex, index);
      },
    });

    return combine(draggableTab, dropTarget);
  }, [board.id, index, onReorder, onDragStart, onDragEnd]);

  return (
    <Tab
      ref={tabRef}
      onClick={() => onTabClick()}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            color: isDragging ? "white" : "inherit", // Ensure text color is visible
          }}
        >
          <Box
            ref={dragHandleRef}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "grab",
              mr: 1,
              px: 0.5,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={(e) => e.stopPropagation()} // Prevent tab click when clicking the drag handle
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          {board.name}
          <Box
            component="span"
            sx={{
              ml: "auto", // Push to the right
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen(e, board.id);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </Box>
        </Box>
      }
      id={`board-tab-${index}`}
      aria-controls={`board-tabpanel-${index}`}
    />
  );
};

export default BoardsPage;
